import React, { useState, useEffect, useMemo } from 'react';
import {
  Input,
  Select,
  DatePicker,
  Button,
  Space,
  Card,
  Tag,
  Drawer,
  Tooltip,
  Badge,
} from 'antd';
import {
  SearchOutlined,
  FilterOutlined,
  ClearOutlined,
  SaveOutlined,
  StarOutlined,
  StarFilled,
  DownloadOutlined,
} from '@ant-design/icons';
import Fuse from 'fuse.js';
import { Registro } from '../types';
import dayjs, { Dayjs } from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;

interface FilterConfig {
  searchText: string;
  lugar: string[];
  categoria: string[];
  usuario: string[];
  dateRange: [Dayjs | null, Dayjs | null] | null;
  estado: string[];
}

interface SavedFilter {
  id: string;
  name: string;
  config: FilterConfig;
  isFavorite: boolean;
}

interface AdvancedSearchProps {
  registros: Registro[];
  onFilteredResults: (filtered: Registro[]) => void;
  onExport?: (filtered: Registro[]) => void;
}

const AdvancedSearch: React.FC<AdvancedSearchProps> = ({
  registros,
  onFilteredResults,
  onExport,
}) => {
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedLugares, setSelectedLugares] = useState<string[]>([]);
  const [selectedCategorias, setSelectedCategorias] = useState<string[]>([]);
  const [selectedUsuarios, setSelectedUsuarios] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [filterName, setFilterName] = useState('');

  // Configurar Fuse.js para búsqueda fuzzy
  const fuse = useMemo(
    () =>
      new Fuse(registros, {
        keys: [
          { name: 'lugar', weight: 2 },
          { name: 'incidencia', weight: 2 },
          { name: 'descripcion', weight: 1.5 },
          { name: 'observaciones', weight: 1 },
          { name: 'usuario', weight: 1 },
          { name: 'categoria', weight: 1.5 },
        ],
        threshold: 0.3,
        includeScore: true,
      }),
    [registros]
  );

  // Obtener opciones únicas para los filtros
  const lugares = useMemo(
    () => Array.from(new Set(registros.map(r => r.lugar).filter(Boolean))).sort(),
    [registros]
  );

  const categorias = useMemo(
    () =>
      Array.from(new Set(registros.map(r => r.categoria).filter(Boolean))).sort(),
    [registros]
  );

  const usuarios = useMemo(
    () => Array.from(new Set(registros.map(r => r.usuario).filter(Boolean))).sort(),
    [registros]
  );

  // Aplicar filtros
  const filteredRegistros = useMemo(() => {
    let results = registros;

    // Búsqueda de texto con Fuse.js
    if (searchText.trim()) {
      const fuseResults = fuse.search(searchText);
      results = fuseResults.map(result => result.item);
    }

    // Filtro por lugar
    if (selectedLugares.length > 0) {
      results = results.filter(r => selectedLugares.includes(r.lugar));
    }

    // Filtro por categoría
    if (selectedCategorias.length > 0) {
      results = results.filter(r => r.categoria && selectedCategorias.includes(r.categoria));
    }

    // Filtro por usuario
    if (selectedUsuarios.length > 0) {
      results = results.filter(r => r.usuario && selectedUsuarios.includes(r.usuario));
    }

    // Filtro por rango de fechas
    if (dateRange && dateRange[0] && dateRange[1]) {
      results = results.filter(r => {
        const fecha = dayjs(r.fecha);
        return fecha.isAfter(dateRange[0]) && fecha.isBefore(dateRange[1]);
      });
    }

    return results;
  }, [
    registros,
    searchText,
    selectedLugares,
    selectedCategorias,
    selectedUsuarios,
    dateRange,
    fuse,
  ]);

  // Notificar cambios al componente padre
  useEffect(() => {
    onFilteredResults(filteredRegistros);
  }, [filteredRegistros, onFilteredResults]);

  // Cargar filtros guardados del localStorage
  useEffect(() => {
    const saved = localStorage.getItem('savedFilters');
    if (saved) {
      try {
        setSavedFilters(JSON.parse(saved));
      } catch (e) {
        console.error('Error al cargar filtros guardados:', e);
      }
    }
  }, []);

  // Limpiar todos los filtros
  const handleClearFilters = () => {
    setSearchText('');
    setSelectedLugares([]);
    setSelectedCategorias([]);
    setSelectedUsuarios([]);
    setDateRange(null);
  };

  // Guardar filtro actual
  const handleSaveFilter = () => {
    if (!filterName.trim()) {
      return;
    }

    const newFilter: SavedFilter = {
      id: Date.now().toString(),
      name: filterName,
      config: {
        searchText,
        lugar: selectedLugares,
        categoria: selectedCategorias,
        usuario: selectedUsuarios,
        dateRange,
        estado: [],
      },
      isFavorite: false,
    };

    const updated = [...savedFilters, newFilter];
    setSavedFilters(updated);
    localStorage.setItem('savedFilters', JSON.stringify(updated));
    setFilterName('');
  };

  // Aplicar filtro guardado
  const handleApplySavedFilter = (filter: SavedFilter) => {
    setSearchText(filter.config.searchText);
    setSelectedLugares(filter.config.lugar);
    setSelectedCategorias(filter.config.categoria);
    setSelectedUsuarios(filter.config.usuario);
    setDateRange(filter.config.dateRange);
    setDrawerVisible(false);
  };

  // Marcar/desmarcar como favorito
  const handleToggleFavorite = (filterId: string) => {
    const updated = savedFilters.map(f =>
      f.id === filterId ? { ...f, isFavorite: !f.isFavorite } : f
    );
    setSavedFilters(updated);
    localStorage.setItem('savedFilters', JSON.stringify(updated));
  };

  // Eliminar filtro guardado
  const handleDeleteFilter = (filterId: string) => {
    const updated = savedFilters.filter(f => f.id !== filterId);
    setSavedFilters(updated);
    localStorage.setItem('savedFilters', JSON.stringify(updated));
  };

  // Contar filtros activos
  const activeFiltersCount =
    (searchText ? 1 : 0) +
    (selectedLugares.length > 0 ? 1 : 0) +
    (selectedCategorias.length > 0 ? 1 : 0) +
    (selectedUsuarios.length > 0 ? 1 : 0) +
    (dateRange ? 1 : 0);

  return (
    <Card style={{ marginBottom: 16 }}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {/* Búsqueda de texto */}
        <Input
          size="large"
          placeholder="Buscar en todos los campos (lugar, incidencia, descripción, usuario...)"
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          allowClear
        />

        {/* Botones de acción */}
        <Space wrap>
          <Badge count={activeFiltersCount} offset={[-5, 5]}>
            <Button
              icon={<FilterOutlined />}
              onClick={() => setDrawerVisible(true)}
            >
              Filtros Avanzados
            </Button>
          </Badge>

          {activeFiltersCount > 0 && (
            <Button icon={<ClearOutlined />} onClick={handleClearFilters}>
              Limpiar Filtros
            </Button>
          )}

          {onExport && filteredRegistros.length > 0 && (
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={() => onExport(filteredRegistros)}
            >
              Exportar ({filteredRegistros.length})
            </Button>
          )}

          <Tag color="blue">
            {filteredRegistros.length} de {registros.length} registros
          </Tag>
        </Space>

        {/* Tags de filtros activos */}
        {activeFiltersCount > 0 && (
          <Space wrap>
            {searchText && (
              <Tag closable onClose={() => setSearchText('')}>
                Búsqueda: "{searchText}"
              </Tag>
            )}
            {selectedLugares.map(lugar => (
              <Tag
                key={lugar}
                closable
                onClose={() =>
                  setSelectedLugares(selectedLugares.filter(l => l !== lugar))
                }
              >
                Lugar: {lugar}
              </Tag>
            ))}
            {selectedCategorias.map(cat => (
              <Tag
                key={cat}
                closable
                onClose={() =>
                  setSelectedCategorias(selectedCategorias.filter(c => c !== cat))
                }
              >
                Categoría: {cat}
              </Tag>
            ))}
            {selectedUsuarios.map(user => (
              <Tag
                key={user}
                closable
                onClose={() =>
                  setSelectedUsuarios(selectedUsuarios.filter(u => u !== user))
                }
              >
                Usuario: {user}
              </Tag>
            ))}
            {dateRange && (
              <Tag closable onClose={() => setDateRange(null)}>
                Fecha: {dateRange[0]?.format('DD/MM/YYYY')} -{' '}
                {dateRange[1]?.format('DD/MM/YYYY')}
              </Tag>
            )}
          </Space>
        )}
      </Space>

      {/* Drawer de filtros avanzados */}
      <Drawer
        title="Filtros Avanzados"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width={400}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {/* Filtro por lugar */}
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Lugar
            </label>
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              placeholder="Seleccionar lugares"
              value={selectedLugares}
              onChange={setSelectedLugares}
              maxTagCount="responsive"
            >
              {lugares.map(lugar => (
                <Option key={lugar} value={lugar}>
                  {lugar}
                </Option>
              ))}
            </Select>
          </div>

          {/* Filtro por categoría */}
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Categoría
            </label>
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              placeholder="Seleccionar categorías"
              value={selectedCategorias}
              onChange={setSelectedCategorias}
              maxTagCount="responsive"
            >
              {categorias.map(cat => (
                <Option key={cat} value={cat}>
                  {cat}
                </Option>
              ))}
            </Select>
          </div>

          {/* Filtro por usuario */}
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Usuario/Guardia
            </label>
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              placeholder="Seleccionar usuarios"
              value={selectedUsuarios}
              onChange={setSelectedUsuarios}
              maxTagCount="responsive"
            >
              {usuarios.map(user => (
                <Option key={user} value={user}>
                  {user}
                </Option>
              ))}
            </Select>
          </div>

          {/* Filtro por rango de fechas */}
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Rango de Fechas
            </label>
            <RangePicker
              style={{ width: '100%' }}
              value={dateRange}
              onChange={value => setDateRange(value as [Dayjs | null, Dayjs | null])}
              format="DD/MM/YYYY"
            />
          </div>

          {/* Guardar filtro */}
          <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Guardar Filtro Actual
            </label>
            <Space.Compact style={{ width: '100%' }}>
              <Input
                placeholder="Nombre del filtro"
                value={filterName}
                onChange={e => setFilterName(e.target.value)}
              />
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSaveFilter}
                disabled={!filterName.trim()}
              >
                Guardar
              </Button>
            </Space.Compact>
          </div>

          {/* Filtros guardados */}
          {savedFilters.length > 0 && (
            <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                Filtros Guardados
              </label>
              <Space direction="vertical" style={{ width: '100%' }}>
                {savedFilters.map(filter => (
                  <Card
                    key={filter.id}
                    size="small"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleApplySavedFilter(filter)}
                    extra={
                      <Space>
                        <Tooltip title={filter.isFavorite ? 'Quitar favorito' : 'Marcar favorito'}>
                          <Button
                            type="text"
                            size="small"
                            icon={
                              filter.isFavorite ? (
                                <StarFilled style={{ color: '#faad14' }} />
                              ) : (
                                <StarOutlined />
                              )
                            }
                            onClick={e => {
                              e.stopPropagation();
                              handleToggleFavorite(filter.id);
                            }}
                          />
                        </Tooltip>
                        <Button
                          type="text"
                          size="small"
                          danger
                          onClick={e => {
                            e.stopPropagation();
                            handleDeleteFilter(filter.id);
                          }}
                        >
                          Eliminar
                        </Button>
                      </Space>
                    }
                  >
                    <strong>{filter.name}</strong>
                  </Card>
                ))}
              </Space>
            </div>
          )}
        </Space>
      </Drawer>
    </Card>
  );
};

export default AdvancedSearch;
